<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue';
import { useUserContext } from '@ilteducation/ilt-auth';
import config from '@/config/ConfigProvider';
import { useLocale } from '@/plugins/translation';
import StudentsSelectionTable from '@/components/user-panel/StudentsSelectionTable.vue';
import WatchedVideos from '@/components/user-panel/WatchedVideos.vue';
import SubjectList from '@/components/videos/SubjectList.vue';
import { storeToRefs } from 'pinia';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import MyCollections from '@/components/collections/MyCollections.vue';
import SharedWithMeCollections from '@/components/collections/SharedWithMeCollections.vue';
import { useElementBounding } from '@vueuse/core';

const { t, l } = useLocale();
const tab = ref(0);

const userContext = useUserContext();
const { user } = storeToRefs(userContext);
const profileUrl = ref(config.value('profileUrl'));
const isEmployee = computed(() => userContext.hasRole('employee'));
const isNotProd = computed(() => config.value('environment') !== 'production');

// Students (i.e. non-employees) see "Shared with me" first; teachers keep "My collections" first.
const collectionTabs = computed(() => {
  const myCollections = {
    anchor: 'collections',
    title: l.my_page.collections_tab.tab_title,
    component: MyCollections,
  };
  const sharedWithMe = {
    anchor: 'shared-with-me',
    title: l.my_page.collections_tab.shared_with_me,
    component: SharedWithMeCollections,
  };
  return isEmployee.value ? [myCollections, sharedWithMe] : [sharedWithMe, myCollections];
});

const tabsContentFrame = ref(null);
const bounds = useElementBounding(tabsContentFrame);
const tabsContentHeight = ref('600px'); // Default height

const tabAnchors = computed(() => {
  const anchors: string[] = [];
  collectionTabs.value.forEach((collectionTab) => anchors.push(collectionTab.anchor));
  anchors.push('watched-videos');
  if (isEmployee.value) {
    anchors.push('students');
  }
  return anchors;
});

const getTabFromHash = (): number => {
  const hash = window.location.hash.replace('#', '');
  if (!hash) return 0;

  const index = tabAnchors.value.indexOf(hash);
  return index >= 0 ? index : 0;
};

const updateUrlHash = (tabIndex: number): void => {
  const anchor = tabAnchors.value[tabIndex];
  if (anchor) {
    const newHash = `#${anchor}`;
    if (window.location.hash !== newHash) {
      window.history.replaceState(null, '', newHash);
    }
  }
};

// Handle browser back/forward navigation
const handleHashChange = (): void => {
  const newTab = getTabFromHash();
  if (newTab !== tab.value) {
    tab.value = newTab;
  }
};

// Watch for tab changes and update URL
watch(
  tab,
  (newTab) => {
    nextTick(() => {
      updateUrlHash(newTab);
    });
  },
  { immediate: false },
);

// Watch for tab anchors changes (when computed values change)
watch(
  tabAnchors,
  () => {
    // Recalculate tab from current hash when available tabs change
    const newTab = getTabFromHash();
    if (newTab !== tab.value) {
      tab.value = newTab;
    }
  },
  { immediate: false },
);

onMounted(() => {
  const { top } = bounds;
  tabsContentHeight.value = `calc(100vh - ${top.value}px)`;

  // Set initial tab from URL hash
  tab.value = getTabFromHash();

  // Listen for hash changes (browser back/forward)
  window.addEventListener('hashchange', handleHashChange);
});

// Cleanup hash change listener on component unmount
onUnmounted(() => {
  window.removeEventListener('hashchange', handleHashChange);
});
</script>

<template>
  <v-container class="my-page-container pt-9 px-0 pb-0">
    <v-row>
      <v-container class="custom-container">
        <v-row>
          <v-col class="pt-7 pb-9 d-flex align-center justify-space-between">
            <h1 id="user-name">{{ user.name }}</h1>
            <div class="text-secondary">
              <v-btn variant="outlined" color="primary" :href="profileUrl" target="_blank" class="edit-btn px-9">
                <template #prepend>
                  <FontAwesomeIcon icon="fa-regular fa-pen" />
                </template>
                {{ t(l.my_page.edit_info) }}
              </v-btn>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </v-row>
    <v-row>
      <div class="tabs-wrapper w-100">
        <v-tabs v-model="tab" class="user-page-tabs ml-auto mr-auto w-100 px-4">
          <v-tab v-for="collectionTab in collectionTabs" :key="collectionTab.anchor" class="px-8 mr-1">
            <FontAwesomeIcon icon="fa-light fa-rectangle-history" class="pr-2" />
            {{ t(collectionTab.title) }}
          </v-tab>
          <v-tab class="px-8 mr-1">
            <FontAwesomeIcon icon="fa-light fa-arrow-rotate-left" class="pr-2" />
            {{ t(l.my_page.watched_films_tab.tab_title) }}
          </v-tab>
          <v-tab v-if="isEmployee" class="px-8 mr-1">
            <FontAwesomeIcon icon="fa-light fa-users" class="pr-2" />
            {{ t(l.my_page.my_students_tab.tab_title) }}
          </v-tab>
          <v-tab v-if="isNotProd" class="px-8">{{ t(l.my_page.progress_tab.tab_title) }}</v-tab>
        </v-tabs>
      </div>
      <div class="tabs-content-frame bg-white">
        <v-window v-model="tab">
          <v-window-item v-for="collectionTab in collectionTabs" :key="collectionTab.anchor">
            <component :is="collectionTab.component" />
          </v-window-item>
          <v-window-item>
            <v-container class="custom-container">
              <v-row>
                <v-col>
                  <WatchedVideos :user-uid="user.uid" />
                </v-col>
              </v-row>
            </v-container>
          </v-window-item>
          <v-window-item v-if="isEmployee">
            <v-container class="custom-container px-0">
              <StudentsSelectionTable />
            </v-container>
          </v-window-item>
          <v-window-item v-if="isNotProd">
            <v-container class="custom-container">
              <v-row>
                <v-col>
                  <!-- UserProgress-->
                  <SubjectList size="large" show-percent />
                </v-col>
              </v-row>
            </v-container>
          </v-window-item>
        </v-window>
      </div>
    </v-row>
  </v-container>
</template>

<style lang="scss" scoped>
@use '@/foundations/theme/settings';
@use '@/foundations/theme/styles' as s;

$MyPageTitleHeight: 140px;
$MyPageTabsHeight: 48px;

.my-page-container {
  max-width: none;
  background-color: rgb(var(--v-theme-surface-container));
}
.custom-container {
  max-width: 81.25rem;
}
.row {
  max-width: none;
  width: auto;
  margin-left: -12px;
  margin-right: -12px;
}

.user-page-tabs {
  max-width: 81.25rem;

  .v-tab {
    text-transform: none;
    letter-spacing: 0;
    transition: all 0.3s ease;
    border-radius: 16px 16px 0 0;
  }
  .v-tab-item--selected {
    color: rgb(var(--v-theme-primary)) !important;
    background: white !important;
    border-radius: 16px 16px 0 0;
    border-color: rgb(var(--v-theme-outline-variant));
    border-width: 1px 1px 0 1px;
    border-style: solid;
  }
  :deep(.v-tab__slider) {
    display: none;
  }
}

.tabs-content-frame {
  width: 100%;
  overflow: hidden;
  min-height: calc(100vh - s.$top-menu-and-safe-area - $MyPageTitleHeight - $MyPageTabsHeight);
}

.tabs-wrapper {
  box-shadow: inset 0 -1px 0 0 rgb(var(--v-theme-outline-variant));
}

.edit-btn {
  width: 117px;
  letter-spacing: 0;
  border-color: rgb(var(--v-theme-primary));
}
</style>
